import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIf, NgForOf } from '@angular/common';
import { GolfEvent } from '../../../models/GolfEvent';
import { LeagueWeek } from '../../../models/LeagueWeek';
import { UpdateGolfEventRequest } from '../../../models/updategolfeventrequest';
import { UpdateLeagueWeekRequest } from '../../../models/updateleagueweekrequest';
import { GolfEventService } from '../../../services/golf-event.service';
import { ApplicationService } from '../../../services/application.service';
import { toCalendarControlFriendlyDate } from '../../../util/dateutil';
import { EVENT_FORMAT } from '../../../models/constants';

interface EditableCourseSlot {
  id:number,
  golfCourseName:string,
  teeLocation:string,
  pinLocations:string,
  gimmeCircleSize:number,
  holesToPlay:string,
  roundsToCount:number,
  maxRoundsToPost:number,
  coursePar:number,
  weekStartDate:string,
  weekEndDate:string
}

@Component({
  selector: 'app-edit-event',
  standalone: true,
  imports: [FormsModule, NgIf, NgForOf],
  templateUrl: './edit-event.component.html',
  styleUrl: './edit-event.component.css'
})
export class EditEventComponent implements OnChanges {
  constructor(private golfEventService:GolfEventService, private applicationService:ApplicationService) {}

  @Input() league:GolfEvent|null = null;
  @Output() saved = new EventEmitter<void>();
  @Output() canceled = new EventEmitter<void>();

  currentStep:string = 'EVENTINFO';
  courseSlots:Array<EditableCourseSlot> = [];

  request:UpdateGolfEventRequest = {
    id: -1,
    eventName: '',
    description: '',
    startDate: '',
    endDate: '',
    entryFee: 0,
    firstPlacePrizeMoney: 0,
    secondPlacePrizeMoney: 0,
    thirdPlacePrizeMoney: 0,
    isTeamEvent: false,
    scoreAggregation: 'SUM',
    useHandicaps: true
  };

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['league'] && this.league) {
      this.currentStep = 'EVENTINFO';
      this.request = {
        id: this.league.id,
        eventName: this.league.eventName,
        description: this.league.description,
        startDate: this.toInputDate(this.league.startDate),
        endDate: this.toInputDate(this.league.endDate),
        entryFee: this.league.entryFee,
        firstPlacePrizeMoney: this.league.firstPlacePrizeMoney,
        secondPlacePrizeMoney: this.league.secondPlacePrizeMoney,
        thirdPlacePrizeMoney: this.league.thirdPlacePrizeMoney,
        isTeamEvent: this.league.isTeamEvent,
        scoreAggregation: this.league.scoreAggregation || 'SUM',
        useHandicaps: this.league.useHandicaps
      };
      await this.loadCourseSlots();
    }
  }

  get needsCourseSetup():boolean {
    if (!this.league) return false;
    return this.league.type === EVENT_FORMAT.SINGLE_COURSE
      || this.league.type === EVENT_FORMAT.MULTI_COURSE
      || this.league.type === EVENT_FORMAT.HYBRID_SCRAMBLE;
  }

  get isHybridScramble():boolean {
    return this.league?.type === EVENT_FORMAT.HYBRID_SCRAMBLE;
  }

  get allowsMultipleCourses():boolean {
    return this.league?.type === EVENT_FORMAT.MULTI_COURSE
      || this.league?.type === EVENT_FORMAT.HYBRID_SCRAMBLE;
  }

  get formatNextLabel():string {
    return this.needsCourseSetup ? 'Next: Course(s) >' : 'Save Event';
  }

  moveToStep(stepName:string):void {
    this.currentStep = stepName;
  }

  async goToNextFromFormat():Promise<void> {
    if (this.needsCourseSetup) {
      this.moveToStep('COURSES');
      return;
    }
    await this.save();
  }

  async loadCourseSlots():Promise<void> {
    if (!this.league || !this.needsCourseSetup) {
      this.courseSlots = [];
      return;
    }

    const weeks:Array<LeagueWeek> = await this.golfEventService.getLeagueWeeks(this.league.id);
    this.courseSlots = weeks.map(week => ({
      id: week.id,
      golfCourseName: week.golfCourseName,
      teeLocation: week.teeLocation,
      pinLocations: week.pinLocations,
      gimmeCircleSize: week.gimmeCircleSize,
      holesToPlay: week.holesToPlay,
      roundsToCount: week.roundsToCount,
      maxRoundsToPost: week.maxRoundsToPost,
      coursePar: week.coursePar,
      weekStartDate: this.toInputDate(week.weekStartDate),
      weekEndDate: this.toInputDate(week.weekEndDate)
    }));
  }

  async save():Promise<void> {
    this.request.entryFee = Number(this.request.entryFee);
    this.request.firstPlacePrizeMoney = Number(this.request.firstPlacePrizeMoney);
    this.request.secondPlacePrizeMoney = Number(this.request.secondPlacePrizeMoney);
    this.request.thirdPlacePrizeMoney = Number(this.request.thirdPlacePrizeMoney);

    if (this.isHybridScramble) {
      this.request.isTeamEvent = false;
      this.request.useHandicaps = true;
      this.request.scoreAggregation = 'AVG';
    }

    this.applicationService.showGlobalSpinner();
    await this.golfEventService.updateEvent(this.request);

    if (this.needsCourseSetup) {
      for (const slot of this.courseSlots) {
        const updateRequest:UpdateLeagueWeekRequest = {
          id: slot.id,
          golfCourseName: slot.golfCourseName,
          teeLocation: slot.teeLocation,
          pinLocations: slot.pinLocations,
          gimmeCircleSize: Number(slot.gimmeCircleSize),
          weekStartDate: this.request.startDate,
          weekEndDate: this.request.endDate,
          holesToPlay: slot.holesToPlay,
          roundsToCount: Number(slot.roundsToCount),
          maxRoundsToPost: Number(slot.maxRoundsToPost),
          coursePar: Number(slot.coursePar)
        };
        await this.golfEventService.updateLeagueWeek(updateRequest);
      }
    }

    this.applicationService.hideGlobalSpinner();
    this.saved.emit();
  }

  cancel():void {
    this.canceled.emit();
  }

  toInputDate(date:Date|string):string {
    if (typeof date === 'string') {
      return date.substring(0, 10);
    }
    return toCalendarControlFriendlyDate(date);
  }
}
